/**
 * paymentsApi — thin client for the Vendor Payments System endpoints
 * (`/api/payments/*`). Centralizes Supabase Bearer-token injection so pages
 * and components don't repeat the getSession() dance.
 *
 * Every method returns the parsed response body (or throws an Error whose
 * message is the server's `error`/`detail` for display in a snackbar).
 */
import axios from 'axios';
import { supabase } from '../utils/supabaseClient';

const api = axios.create({ baseURL: '/api/payments' });

// Attach the current Supabase access token to every request.
api.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Surface a useful message on failure.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const body = err.response?.data;
    const message = body?.error || body?.detail || err.message || 'Request failed';
    const e = new Error(message);
    e.status = err.response?.status;
    e.body = body; // callers can inspect e.body.blockers, etc.
    return Promise.reject(e);
  }
);

const paymentsApi = {
  whoami: () => api.get('/whoami').then((r) => r.data),

  // Batches
  /**
   * Two-mode import (server: POST /batches/import). confirm=false returns a
   * preview with no writes; confirm=true creates the batch. A duplicate file
   * returns 409 with { duplicateOf, preview } until acknowledgeDuplicate=true.
   */
  importBatch: (file, { confirm = false, acknowledgeDuplicate = false } = {}) => {
    const form = new FormData();
    form.append('file', file);
    form.append('confirm', String(confirm));
    form.append('acknowledgeDuplicate', String(acknowledgeDuplicate));
    return api.post('/batches/import', form).then((r) => r.data);
  },
  listBatches: (params = {}) => api.get('/batches', { params }).then((r) => r.data.batches),
  getBatch: (id) => api.get(`/batches/${id}`).then((r) => r.data.batch),
  getRows: (id, params = {}) => api.get(`/batches/${id}/rows`, { params }).then((r) => r.data),
  getRuleResults: (id, params = {}) =>
    api.get(`/batches/${id}/rule-results`, { params }).then((r) => r.data.ruleResults),
  getProviderTotals: (id) => api.get(`/batches/${id}/provider-totals`).then((r) => r.data.providerTotals),
  getAuditEvents: (id) => api.get(`/batches/${id}/audit-events`).then((r) => r.data.events),
  runRules: (id) => api.post(`/batches/${id}/run-rules`).then((r) => r.data),
  submitToFinance: (id) => api.post(`/batches/${id}/submit-to-finance`).then((r) => r.data),
  patchBatch: (id, body) => api.patch(`/batches/${id}`, body).then((r) => r.data.batch),

  // Rows
  patchRow: (id, body) => api.patch(`/rows/${id}`, body).then((r) => r.data),
  excludeRow: (id, body) => api.post(`/rows/${id}/exclude`, body).then((r) => r.data),
  includeRow: (id) => api.post(`/rows/${id}/include`).then((r) => r.data),
  getRowEditHistory: (id) => api.get(`/rows/${id}/edit-history`).then((r) => r.data.edits),

  // Mappings
  listProviderMappings: () => api.get('/mappings/providers').then((r) => r.data.mappings),
  createProviderMapping: (body) => api.post('/mappings/providers', body).then((r) => r.data),
  listServiceTypeMappings: () => api.get('/mappings/service-types').then((r) => r.data.mappings),
  createServiceTypeMapping: (body) => api.post('/mappings/service-types', body).then((r) => r.data),

  // Rule results
  resolveRuleResult: (id) => api.post(`/rule-results/${id}/resolve`).then((r) => r.data),
  overrideRuleResult: (id, reason) =>
    api.post(`/rule-results/${id}/override`, { reason }).then((r) => r.data),

  // Finance workflow (Phase 5)
  beginReview: (id) => api.post(`/batches/${id}/begin-review`).then((r) => r.data),
  approveBatch: (id) => api.post(`/batches/${id}/approve`).then((r) => r.data),
  rejectBatch: (id, reason) => api.post(`/batches/${id}/reject`, { reason }).then((r) => r.data),
  returnBatch: (id, reason) => api.post(`/batches/${id}/return-to-operations`, { reason }).then((r) => r.data),
  approveProvider: (id, providerId) => api.post(`/batches/${id}/providers/${providerId}/approve`).then((r) => r.data),
  rejectProvider: (id, providerId, reason) => api.post(`/batches/${id}/providers/${providerId}/reject`, { reason }).then((r) => r.data),
  approveRow: (rowId) => api.post(`/rows/${rowId}/approve`).then((r) => r.data),
  rejectRow: (rowId, reason) => api.post(`/rows/${rowId}/reject`, { reason }).then((r) => r.data),
  listAdjustments: (id) => api.get(`/batches/${id}/adjustments`).then((r) => r.data.adjustments),
  addAdjustment: (id, body) => api.post(`/batches/${id}/adjustments`, body).then((r) => r.data),
  deleteAdjustment: (id, adjId) => api.delete(`/batches/${id}/adjustments/${adjId}`).then((r) => r.data),
  listApprovals: (id) => api.get(`/batches/${id}/approvals`).then((r) => r.data.approvals),

  // Statements (Phase 6)
  generateStatements: (id) => api.post(`/batches/${id}/generate-statements`).then((r) => r.data.statements),
  listStatements: (id) => api.get(`/batches/${id}/statements`).then((r) => r.data.statements),
  previewStatement: (statementId) => api.get(`/contractor-statements/${statementId}/preview`).then((r) => r.data.url),

  // Statement emails (Phase 6 PR2)
  approveEmails: (id) => api.post(`/batches/${id}/approve-emails`).then((r) => r.data),
  sendEmails: (id) => api.post(`/batches/${id}/send-emails`).then((r) => r.data),
  sendTestEmail: (id, to) => api.post(`/batches/${id}/send-test-email`, to ? { to } : {}).then((r) => r.data),
  sendStatement: (statementId) => api.post(`/contractor-statements/${statementId}/send`).then((r) => r.data),
  resendStatement: (statementId) => api.post(`/contractor-statements/${statementId}/resend`).then((r) => r.data),

  // QBO export (Phase 6 PR3)
  generateQboExport: (id, reexportReason) => api.post(`/batches/${id}/generate-qbo-export`, reexportReason ? { reexportReason } : {}).then((r) => r.data),
  listQboExports: (id) => api.get(`/batches/${id}/qbo-exports`).then((r) => r.data.exports),
  downloadQboExport: (exportId) => api.get(`/qbo-exports/${exportId}/download`).then((r) => r.data.url),
  closeBatch: (id) => api.post(`/batches/${id}/close`).then((r) => r.data.batch),
};

export default paymentsApi;
